import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { Search, Plus, Upload, Trash2, Loader2, Calendar, ExternalLink, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReportCard = ({ report, onDelete }) => {
  const dateFormatted = report.dateAdded
    ? new Date(report.dateAdded).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : '';
  return (
    <div className="relative group h-full" data-testid={`availability-report-${report.id}`}>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(report); }}
        className="absolute top-2 right-2 z-10 w-7 h-7 rounded-md bg-white/90 border border-[#E2E8F0] flex items-center justify-center text-[#94A3B8] hover:text-[#FF3B30] hover:border-[#FF3B30] transition-colors"
        data-testid={`availability-report-delete-${report.id}`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
      <Card
        onClick={() => window.open(report.contentFile, '_blank', 'noopener,noreferrer')}
        className="bg-white border-[#E2E8F0] overflow-hidden h-full flex flex-col transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(14,165,233,0.18)] cursor-pointer"
      >
        <div className="h-1 w-full bg-gradient-to-r from-[#0EA5E9] to-[#4ECDC4]" />
        <CardContent className="p-0 flex flex-col flex-1">
          <div className="p-5 flex-1 flex flex-col">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide bg-[#E0F2FE] text-[#0284C7] self-start mb-2">
              <Calendar className="w-3 h-3" /> Availability Report
            </span>
            <h3 className="text-[#1A1A1A] font-bold text-base leading-tight mb-2 group-hover:text-[#0284C7] transition-colors" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>
              {report.title}
            </h3>
            <p className="text-[#666] text-sm leading-relaxed flex-1 line-clamp-3">{report.description}</p>
          </div>
          <div className="px-5 py-3 border-t border-[#f0f0f0] flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs text-[#999]"><Calendar className="w-3 h-3" />{dateFormatted}</span>
            <span className="flex items-center gap-1 text-sm font-semibold text-[#0284C7] group-hover:underline">Open <ExternalLink className="w-3 h-3" /></span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const AvailabilityReportsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ file: null, title: '', description: '' });

  const fetchReports = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/resources`, { headers: getAuthHeaders() });
      const list = res.data
        .filter((r) => r.type === 'availability-report')
        .map((r) => ({ ...r, contentFile: `${API}/public/resources/${r.id}/view` }));
      setReports(list);
    } catch (e) {
      toast.error('Failed to load availability reports');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const filtered = useMemo(() => {
    if (!search.trim()) return reports;
    const q = search.toLowerCase();
    return reports.filter((r) => r.title.toLowerCase().includes(q) || r.description.toLowerCase().includes(q));
  }, [reports, search]);

  const handleUpload = async () => {
    if (!form.file) { toast.error('Please choose a file'); return; }
    if (!form.title.trim() || !form.description.trim()) { toast.error('Title and description are required'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('title', form.title.trim());
      fd.append('description', form.description.trim());
      fd.append('type', 'availability-report');
      await axios.post(`${API}/resources`, fd, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
      toast.success('Availability report published');
      setForm({ file: null, title: '', description: '' });
      setDialogOpen(false);
      fetchReports();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (report) => {
    if (!window.confirm(`Delete "${report.title}"?`)) return;
    try {
      await axios.delete(`${API}/resources/${report.id}`, { headers: getAuthHeaders() });
      toast.success('Report deleted');
      setReports((prev) => prev.filter((r) => r.id !== report.id));
    } catch (e) {
      toast.error('Failed to delete report');
    }
  };

  return (
    <div className="space-y-6" data-testid="availability-reports-page">
      {/* Header */}
      <div className="rounded-xl overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#0B3B4A] to-[#0EA5E9] p-8 flex items-center justify-between">
        <div>
          <p className="text-[#7DD3FC] text-[11px] font-semibold uppercase tracking-[0.14em] mb-2">Players Health NIL</p>
          <h1 className="text-white text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>
            Availability Reports
          </h1>
          <p className="text-[#CBD5E1] text-sm max-w-xl">Weekly conference availability reports. Upload PDF or HTML files and share them with the team.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#4CD6BE] text-[#0F172A] hover:bg-[#3BC0A9] font-semibold" data-testid="upload-report-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Upload Report
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white sm:max-w-md" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle className="text-[#0F172A]">Upload Availability Report</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="p-3 bg-[#E0F2FE] border border-[#BAE6FD] rounded-lg text-xs text-[#0369A1]">
                Accepts PDF or HTML files. These will appear in the Availability Reports section and the NIL Resource Library.
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#475569] text-sm">File (PDF or HTML)</Label>
                <input
                  type="file"
                  accept=".pdf,.html,.htm"
                  onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                  className="block w-full text-sm text-[#475569] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#EEF0FF] file:text-[#6B5CE7] hover:file:bg-[#E0E4FF] cursor-pointer border border-[#E2E8F0] rounded-md"
                  data-testid="report-file-input"
                />
                {form.file && <p className="text-xs text-[#64748B] flex items-center gap-1"><FileText className="w-3 h-3" />{form.file.name}</p>}
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#475569] text-sm">Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. MAC Conference Availability (Wk 0-3)" className="border-[#E2E8F0]" data-testid="report-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#475569] text-sm">Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short summary of this report" className="border-[#E2E8F0] min-h-[70px]" data-testid="report-desc-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleUpload} disabled={uploading} className="bg-[#6B5CE7] text-white hover:bg-[#5B4CD7] w-full" data-testid="report-upload-submit">
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Publish Report</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search availability reports..." className="pl-10 border-[#E2E8F0]" data-testid="report-search-input" />
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#94A3B8]"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#E2E8F0] rounded-xl">
          <Calendar className="w-10 h-10 text-[#CBD5E1] mx-auto mb-3" />
          <p className="text-[#475569] font-medium mb-1">No availability reports yet</p>
          <p className="text-[#94A3B8] text-sm">Click "Upload Report" to add the first one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((r) => <ReportCard key={r.id} report={r} onDelete={handleDelete} />)}
        </div>
      )}
    </div>
  );
};

export default AvailabilityReportsPage;
